// Fiche d'un constat : grille des réponses contre la version du formulaire emportée, photos (ajout
// d'une photo JPEG ou PNG sur un point qui l'exige), mesures correctives (demande avec délai en jours
// ouvrés, clôture), décision sur un conflit de synchronisation (retenir ou écarter), transmission à
// l'autorité (document PDF, notification), sans sanction.
import { REPONSES_POINT, type ReponsePoint } from '@cc/contrats';
import { Bandeau, Bouton, Champ, DialogueConfirmation, FichierChamp, Lien, LienBouton, Panneau, PuceEtat, Selection, ZoneTexte } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { ajouterPhoto, cloturerMesure, creerMesure, INVALIDER_INSPECTIONS, trancherConstat, transmettreConstat, useConstat } from './api';
import { photosManquantes, TON_CONCLUSION, TON_CONSTAT, TON_MESURE } from './logique';
import type { ConstatDetail } from './types';
import styles from '../proprete/proprete.module.css';
import commun from '../../commun/commun.module.css';

function Photos({ k }: { k: ConstatDetail }) {
  const { contexte } = useSession();
  const manquantes = photosManquantes(k.formulaire_points, k.reponses);
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [point, setPoint] = useState(manquantes[0]?.code ?? '');
  const action = useAction({
    executer: () => {
      const f = fichiers[0];
      if (!f) return Promise.reject(new Error('Choisir une photo'));
      return ajouterPhoto(k.id, f, point || undefined);
    },
    succes: 'Photo rattachée au constat.',
    invalider: INVALIDER_INSPECTIONS,
    apres: () => setFichiers([]),
  });
  return (
    <Panneau titre={`Photos (${k.photos_detail.length})`} niveau={3}>
      {manquantes.length > 0 ? <Bandeau type="info">Photo attendue pour : {manquantes.map((p) => p.libelle).join(', ')}.</Bandeau> : null}
      {k.photos_detail.length === 0 ? <p className={commun.na}>Aucune photo.</p> : (
        <ul className={styles.pointsListe}>
          {k.photos_detail.map((p) => (
            <li key={p.id}><Lien href={`/documents/${p.id}`}>{p.titre}</Lien> <span className={commun.na}>({p.type_mime}, {Math.round(p.taille / 1024)} ko, {dateHeure(p.cree_le)})</span></li>
          ))}
        </ul>
      )}
      {peut(contexte, 'inspection', 'creer') && k.etat !== 'transmis' && k.etat !== 'ecarte' ? (
        <>
          <Selection libelle="Point concerné" options={k.formulaire_points.map((p) => ({ valeur: p.code, libelle: p.libelle }))} optionVide="Photo générale" value={point} onChange={(e) => setPoint(e.target.value)} facultatif />
          <FichierChamp libelle="Photo (JPEG ou PNG)" fichiers={fichiers} onFichiers={setFichiers} multiple={false} accept=".jpg,.jpeg,.png" tailleMax={20 * 1024 * 1024} />
          {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
          <Bouton variante="secondaire" chargement={action.enCours} disabled={fichiers.length === 0} onClick={() => action.lancer(undefined)}>Ajouter la photo</Bouton>
        </>
      ) : null}
    </Panneau>
  );
}

function Mesures({ k }: { k: ConstatDetail }) {
  const { contexte } = useSession();
  const [v, setV] = useState({ description: '', delai: '5', point: '' });
  const demande = useAction({
    executer: () => creerMesure(k.id, { description: v.description, delai_jours: Number(v.delai), point_code: v.point || undefined }),
    succes: (m) => `Mesure demandée, échéance le ${date(m.echeance)} (${m.delai_jours} jours ouvrés).`,
    invalider: INVALIDER_INSPECTIONS,
    apres: () => setV({ description: '', delai: '5', point: '' }),
  });
  const cloture = useAction({ executer: (p: { id: string; etat: 'realisee' | 'non_realisee' }) => cloturerMesure(p.id, p.etat), succes: (m) => `Mesure ${m.etat_libelle.toLowerCase()}.`, invalider: INVALIDER_INSPECTIONS });
  const peutModifier = peut(contexte, 'inspection', 'modifier');
  return (
    <Panneau titre={`Mesures correctives (${k.mesures_detail.length})`} niveau={3}>
      {k.mesures_detail.length === 0 ? <p className={commun.na}>Aucune mesure corrective demandée.</p> : (
        <ul className={styles.pointsListe}>
          {k.mesures_detail.map((m) => (
            <li key={m.id}>
              <PuceEtat ton={TON_MESURE[m.etat]} libelle={m.etat_libelle} />
              <span>
                {m.description} · échéance <span className={m.echue ? styles.motif : undefined}>{date(m.echeance)}{m.echue ? ' (dépassée)' : ''}</span> ({m.delai_jours} jours ouvrés)
                {m.realisee_le ? `, réalisée le ${date(m.realisee_le)}` : ''}
                {peutModifier && (m.etat === 'demandee' || m.etat === 'transmise') ? (
                  <> <Bouton variante="secondaire" petit chargement={cloture.enCours} onClick={() => cloture.lancer({ id: m.id, etat: 'realisee' })}>Réalisée</Bouton> <Bouton variante="secondaire" petit chargement={cloture.enCours} onClick={() => cloture.lancer({ id: m.id, etat: 'non_realisee' })}>Non réalisée</Bouton></>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {peutModifier && k.etat !== 'ecarte' && k.etat !== 'conflit' ? (
        <div className={styles.grilleFormulaire}>
          <Champ libelle="Mesure demandée" value={v.description} onChange={(e) => setV((x) => ({ ...x, description: e.target.value }))} erreur={demande.erreursChamps.find((e) => e.champ === 'description')?.message} required />
          <Champ libelle="Délai (jours ouvrés)" type="number" value={v.delai} onChange={(e) => setV((x) => ({ ...x, delai: e.target.value }))} required />
          <Selection libelle="Point concerné" options={k.formulaire_points.map((p) => ({ valeur: p.code, libelle: p.libelle }))} optionVide="Constat entier" value={v.point} onChange={(e) => setV((x) => ({ ...x, point: e.target.value }))} facultatif />
          <Bouton variante="secondaire" chargement={demande.enCours} disabled={v.description.trim().length < 3} onClick={() => demande.lancer(undefined)}>Demander la mesure</Bouton>
          {demande.messageErreur ? <Bandeau type="erreur">{demande.messageErreur}</Bandeau> : null}
        </div>
      ) : null}
    </Panneau>
  );
}

function Decisions({ k }: { k: ConstatDetail }) {
  const { contexte } = useSession();
  const [dialogue, setDialogue] = useState<'transmettre' | 'retenir' | 'ecarter' | null>(null);
  const [message, setMessage] = useState('');
  const transmission = useAction({ executer: () => transmettreConstat(k.id, { message: message || undefined }), succes: (r) => `Constat transmis à l'autorité ; document ${r.document.code_verification ?? ''} produit.`, invalider: INVALIDER_INSPECTIONS, apres: () => setDialogue(null) });
  const decision = useAction({ executer: (d: 'retenir' | 'ecarter') => trancherConstat(k.id, d, message || undefined), succes: (r) => `Constat ${r.etat_libelle.toLowerCase()}.`, invalider: INVALIDER_INSPECTIONS, apres: () => setDialogue(null) });
  return (
    <>
      {k.etat === 'conflit' && peut(contexte, 'inspection', 'valider') ? <><Bouton variante="principal" onClick={() => setDialogue('retenir')}>Retenir ce constat</Bouton><Bouton variante="secondaire" onClick={() => setDialogue('ecarter')}>Écarter</Bouton></> : null}
      {(k.etat === 'synchronise' || k.etat === 'retenu') && peut(contexte, 'inspection', 'transmettre') ? <Bouton variante="principal" onClick={() => setDialogue('transmettre')}>Transmettre à l&apos;autorité</Bouton> : null}
      <DialogueConfirmation ouvert={dialogue === 'transmettre'} titre="Transmettre le constat à l'autorité" message="Un document PDF est produit et rattaché au constat ; le maire ou le président est notifié ; l'ordre de mission est clos. Aucune sanction n'est prononcée." libelleConfirmer="Transmettre" enCours={transmission.enCours} onConfirmer={() => transmission.lancer(undefined)} onAnnuler={() => setDialogue(null)}>
        <ZoneTexte libelle="Message à l'autorité" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} facultatif />
        {transmission.messageErreur ? <Bandeau type="erreur">{transmission.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
      <DialogueConfirmation ouvert={dialogue === 'retenir' || dialogue === 'ecarter'} titre={dialogue === 'retenir' ? 'Retenir la version de l\'appareil' : 'Écarter la version de l\'appareil'} message="Les deux versions restent lisibles dans l'historique ; aucune donnée n'est écrasée (R9)." libelleConfirmer={dialogue === 'retenir' ? 'Retenir' : 'Écarter'} variante={dialogue === 'ecarter' ? 'danger' : 'principal'} enCours={decision.enCours} onConfirmer={() => decision.lancer(dialogue === 'retenir' ? 'retenir' : 'ecarter')} onAnnuler={() => setDialogue(null)}>
        <Champ libelle="Motif" value={message} onChange={(e) => setMessage(e.target.value)} facultatif />
        {decision.messageErreur ? <Bandeau type="erreur">{decision.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

export function PageConstat() {
  const { id = '' } = useParams();
  const fiche = useConstat(id);
  const k = fiche.data;
  const parCode = new Map((k?.formulaire_points ?? []).map((p) => [p.code, p]));
  return (
    <>
      <EnTetePage etiquette="Inspections et contrôles" titre={k ? `Constat du contrôle ${k.ordre.numero ?? ''}` : 'Constat'}
        description={k ? <span className={commun.ligne}><PuceEtat ton={TON_CONCLUSION[k.conclusion]} libelle={k.conclusion_libelle} /><PuceEtat ton={TON_CONSTAT[k.etat]} libelle={k.etat_libelle} /><span>{k.ordre.objet_libelle ?? ''}</span></span> : null}
        actions={<>{k ? <LienBouton href={`/inspections/ordres/${k.ordre.id}`} icone={ArrowLeft} variante="secondaire">Ordre de mission</LienBouton> : null}{k ? <Decisions k={k} /> : null}</>} />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} lot="L3-B" fonction="La fiche du constat">
        {k ? (
          <div className={commun.deuxColonnes}>
            <div className={commun.pile}>
              {k.etat === 'conflit' ? <Bandeau type="erreur">Constat reçu d&apos;un appareil alors que l&apos;ordre avait changé (version attendue {k.version_attendue ?? '?'}). Retenez-le ou écartez-le ; rien n&apos;est écrasé.</Bandeau> : null}
              <Panneau titre="Grille du constat">
                <dl className={commun.definitions}>
                  <dt>Établi le</dt><dd>{dateHeure(k.effectue_le)} par {k.auteur.nom ?? 'inconnu'} ({k.origine === 'synchronisation' ? 'appareil de terrain' : 'bureau'})</dd>
                  <dt>Reçu le</dt><dd>{dateHeure(k.recu_le)}</dd>
                  <dt>Formulaire</dt><dd>version {k.formulaire.version}</dd>
                  {k.decision ? <><dt>Décision</dt><dd>{k.decision.motif ?? 'sans motif'} ({dateHeure(k.decision.le)})</dd></> : null}
                  {k.transmis_le ? <><dt>Transmis le</dt><dd>{dateHeure(k.transmis_le)}</dd></> : null}
                </dl>
                <table className={styles.grilleConstat} aria-label="Réponses du constat">
                  <thead><tr><th scope="col">Point de contrôle</th><th scope="col">Réponse</th><th scope="col">Observation</th><th scope="col">Photo</th></tr></thead>
                  <tbody>
                    {k.reponses.map((r) => (
                      <tr key={r.code}>
                        <th scope="row">{parCode.get(r.code)?.libelle ?? r.code}</th>
                        <td><PuceEtat ton={r.reponse === 'non_conforme' ? 'retard' : r.reponse === 'conforme' ? 'ok' : 'neutre'} libelle={REPONSES_POINT[r.reponse as ReponsePoint] ?? r.reponse} /></td>
                        <td>{r.observation ?? ''}</td>
                        <td>{r.photo_document_id ? <Lien href={`/documents/${r.photo_document_id}`}>photo</Lien> : parCode.get(r.code)?.photo_exigee && r.reponse !== 'sans_objet' ? 'attendue' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {k.observations ? <p>{k.observations}</p> : null}
              </Panneau>
            </div>
            <div className={commun.pile}>
              <Photos k={k} />
              <Mesures k={k} />
            </div>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
